// @owners { team: patients-team }
import { type QueryClient } from '@tanstack/react-query';
import { getPrescriptions } from '~shared/features/prescriptions/selectors/getPrescriptions';
import { getIsScheduleableByPrescriptionID } from '~shared/features/prescriptions/selectors/getScheduleableForPrescriptionID';
import { type Prescription, type ReduxStateShared } from '~shared/types';

export type ScheduleablePrescriptionsProps = { nextAvailableDateAddressID?: number };

/**
 * Pure scheduleable-prescription list. Use from imperative code (e.g. NAD param resolution) and from
 * {@link useScheduleablePrescriptions}; keep {@link getScheduleablePrescriptions} for Reselect graphs until those migrate.
 *
 * When `queryClient` is passed, scheduleability uses NAD data from the React Query checkout cache (strategy A).
 */
export const computeScheduleablePrescriptions = (
  state: ReduxStateShared,
  props: ScheduleablePrescriptionsProps,
  queryClient?: QueryClient,
): Prescription[] => {
  const prescriptions = getPrescriptions(state);
  const isScheduleableByPrescriptionID = getIsScheduleableByPrescriptionID(state, props, queryClient);
  return prescriptions.filter((rx) => isScheduleableByPrescriptionID[rx.id] || false);
};

/**
 * When `queryClient` is passed, scheduleability uses NAD data from the React Query checkout cache.
 */
/**
 * @deprecated Prefer {@link computeScheduleablePrescriptions} for new call sites.
 * Kept as a named export for existing Reselect graphs until those migrate.
 *
 * When `queryClient` is passed, scheduleability uses NAD data from the React Query checkout cache.
 */
export const getScheduleablePrescriptions = (
  state: ReduxStateShared,
  props: ScheduleablePrescriptionsProps,
  queryClient?: QueryClient,
): Prescription[] => computeScheduleablePrescriptions(state, props, queryClient);

export const getScheduleableNonEssentialPrescriptions = (
  state: ReduxStateShared,
  props: ScheduleablePrescriptionsProps,
  queryClient?: QueryClient,
): Prescription[] => {
  return computeScheduleablePrescriptions(state, props, queryClient).filter((rx) => !rx.is_add_on_otc);
};
